import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import useAppDarkMode from "../../hooks/useAppDarkMode";

export default function AppModal({
	open,
	onClose,
	title,
	subtitle,
	children,
	footer,
	maxWidth = 560,
	hideHeader = false,
	bodyClassName = "",
}) {
	const darkMode = useAppDarkMode();
	const resolvedWidth =
		typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;

	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e) => {
			if (e.key === "Escape") onClose();
			if (e.key !== "Backspace") return;

			const el = e.target;
			const tag = el?.tagName?.toLowerCase();
			if (tag === "input" || tag === "textarea") {
				if (!el.value) e.preventDefault();
				return;
			}
			e.preventDefault();
		};
		document.addEventListener("keydown", onKey);
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKey);
			document.body.style.overflow = "";
		};
	}, [open, onClose]);

	if (!open) return null;

	return createPortal(
		<div
			className="app-modal-overlay tailwick-app"
			data-theme={darkMode ? "dark" : "light"}
			role="dialog"
			aria-modal="true"
			aria-labelledby={hideHeader ? undefined : "app-modal-title"}
			onClick={onClose}
		>
			<div
				className="app-modal"
				style={{ width: `min(100%, ${resolvedWidth})`, maxWidth: resolvedWidth }}
				onClick={(e) => e.stopPropagation()}
			>
				{!hideHeader && (
					<div className="app-modal__header">
						<div className="min-w-0">
							<h2
								id="app-modal-title"
								className="m-0 text-lg font-bold text-default-900 dark:text-default-50"
							>
								{title}
							</h2>
							{subtitle && (
								<p className="mb-0 mt-1 text-sm text-default-500 dark:text-default-400">
									{subtitle}
								</p>
							)}
						</div>
						<button
							type="button"
							className="btn-ghost shrink-0 !p-2"
							onClick={onClose}
							aria-label="Fechar"
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M18 6L6 18M6 6l12 12" />
							</svg>
						</button>
					</div>
				)}
				<div className={`app-modal__body ${bodyClassName}`.trim()}>{children}</div>
				{footer && <div className="app-modal__footer">{footer}</div>}
			</div>
		</div>,
		document.body
	);
}
